import type { GreetingCardCustomization, GreetingCardStorySlide, OrderGiftGreeting } from '@consumer/types/serializers'
import { resizeImageFile } from '@corp/composables/image'
import { useUploader } from '@corp/composables/uploader'
import { decodeImage, Fit, Layout } from '@rive-app/webgl2'

import { EDITING_TEXT } from '@admin/dialogs/consumer/greetingCards/types'
import { useScreen } from '@consumer/helpers/screen'
import { asyncOpenModal } from '@consumer/services/modals'
import type { OrderGiftTemplate } from '@consumer/services/order'
import CustomizerApi from '@consumer/api/CustomizerApi'
import axios from '@shared/axios'
import { notifyError } from '@shared/errors'
import type { GreetingCardWithCachedRiveFile, SenderFlowModalProps, SenderPreviewModalProps } from './types'
import placeholderPhotoUrl from './placeholderPhoto.jpg'
import placeholderPhotoTransparentUrl from './placeholderPhoto_transparent.png'

const BASE64_DATA_URL_RE = /^data:[^,]*;base64,/

// Pulls a non-PII description of the input out of a string that might be a
// signed S3 URL (auth tokens in query params), a blob: URL, etc. We only ever
// log the scheme + length, plus the data URL mediatype prefix when present.
function describeBase64Input (input: string) {
  const colonIndex = input.indexOf(':')
  const scheme = colonIndex > 0 && colonIndex <= 16 ? input.slice(0, colonIndex) : 'none'
  const mediatypeMatch = input.match(/^data:([^,]{0,64}),/)
  return {
    length: input.length,
    scheme,
    mediatype: mediatypeMatch?.[1] ?? null,
  }
}

export async function base64ToRiveImage (base64String: string) {
  // Only the base64 payload of a data URL is valid input for atob(). If the
  // caller hands us anything else (a regular https URL, a non-base64 data URL,
  // a string with whitespace/garbage), atob throws InvalidCharacterError and
  // crashes the recipient flow. Bail out gracefully instead.
  if (!BASE64_DATA_URL_RE.test(base64String)) {
    notifyError(new Error('base64ToRiveImage: input is not a base64 data URL'), {
      extra: describeBase64Input(base64String),
    })
    return null
  }

  const base64WithoutPrefix = base64String.replace(BASE64_DATA_URL_RE, '')

  let binaryString: string
  try {
    binaryString = atob(base64WithoutPrefix)
  }
  catch (e) {
    notifyError(e, { extra: describeBase64Input(base64String) })
    return null
  }

  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return await decodeImage(new Uint8Array(bytes.buffer))
}

export async function uploadPhotoToServer (base64String: string) {
  // Save the image in the server
  const uploader = useUploader()
  const response = await fetch(base64String)
  const blob = await response.blob()
  const extension = blob.type.split('/')[1] || 'jpg'
  let file = new File([blob], `story_slide_${(new Date()).getTime()}.${extension}`, { type: blob.type })

  // Make the file smaller before uploading to the server.
  file = await resizeImageFile(file, 280, 340)

  return await uploader.upload(file)
}

const MIME_TO_EXTENSION: Record<string, string> = {
  'audio/mp4': 'mp4',
  'video/mp4': 'mp4',
  'audio/webm': 'webm',
  'video/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
}

export async function uploadVoiceMemoToServer (base64String: string) {
  const uploader = useUploader()
  const response = await fetch(base64String)
  const blob = await response.blob()
  const declaredType = base64String.match(/^data:([^;,]+)/)?.[1]?.toLowerCase()
  const container = (declaredType ?? blob.type).split(';')[0].trim()
  const extension = MIME_TO_EXTENSION[container] ?? 'webm'
  const file = new File([blob], `voice_memo_${(new Date()).getTime()}.${extension}`, { type: container || blob.type })
  return await uploader.upload(file)
}

export async function downloadPhotoFromServer (attachment?: GreetingCardStorySlide['photo']) {
  if (!attachment) return
  if (typeof attachment === 'string') return

  // Use the Rails Active Storage proxy to avoid CORS issues with direct S3 URLs
  const proxyUrl = `/rails/active_storage/blobs/proxy/${attachment.signedId}/${attachment.filename}`
  const response = await fetch(proxyUrl)
  const blob = await response.blob()

  return await new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsDataURL(blob)
  })
}

export async function downloadPlaceholderPhoto (transparent = false) {
  const response = await fetch(transparent ? placeholderPhotoTransparentUrl : placeholderPhotoUrl)
  const blob = await response.blob()

  const base64 = await new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsDataURL(blob)
  })

  // Bundled placeholder asset — base64ToRiveImage cannot fail here.
  return (await base64ToRiveImage(base64))!
}

export async function downloadCustomizationPhotosFromServer (customization?: GreetingCardCustomization) {
  if (customization?.storySlides) {
    customization.storySlides = await Promise.all(
      customization.storySlides.map(async slide => ({
        ...slide,
        base64Photo: slide.base64Photo || await downloadPhotoFromServer(slide.photo),
      })),
    )
  }
}

export function riveLayout () {
  const { isMobile } = $(useScreen())
  return isMobile ? new Layout({ fit: Fit.Layout }) : undefined
}

export function calculateCurrentSlideTextFontSize (currentSlideText: string) {
  return currentSlideText.length < 100 ? 22 : 20
}

export function calculateNoteFontSize (isMobile: boolean) {
  return isMobile ? 35 : 80
}

export function calculateMobileEditingOffset (editingText: EDITING_TEXT) {
  const keyboardOffsetMap: Partial<Record<EDITING_TEXT, number>> = {
    [EDITING_TEXT.TITLE]: 0,
    [EDITING_TEXT.RECIPIENT]: 200,
    [EDITING_TEXT.SENDER]: 100,
    [EDITING_TEXT.NOTE]: 100,
    [EDITING_TEXT.SIGN_OFF]: 0,
    [EDITING_TEXT.SIGNATURE]: 0,
    [EDITING_TEXT.SLIDE_TEXT]: 0,
  }

  return keyboardOffsetMap[editingText] || 0
}

export function applyCustomization (customization: GreetingCardCustomization | undefined, greeting: OrderGiftGreeting) {
  if (!customization) return

  greeting.greetingCardCustomization = customization
  greeting.message = customization.note
  greeting.to = customization.recipient
  greeting.from = customization.sender
}

export async function loadGiftly (orderGift: OrderGiftTemplate, { force }: { force?: boolean } = {}) {
  if (!orderGift.giftlyMetadata?.designName) return
  if (orderGift.giftlyImageBase64 && !force) return

  const giftlyImageResponse = await axios.post(
    CustomizerApi.giftlyImage.path(),
    {
      amount: orderGift.amount,
      giftly_metadata: orderGift.giftlyMetadata,
      giftly_design_name: orderGift.giftlyMetadata.designName,
    },
    {
      responseType: 'blob',
    },
  )

  const blob = await giftlyImageResponse.data

  const base64 = await new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsDataURL(blob)
  })

  orderGift.giftlyImageBase64 = base64
}

export async function openSenderFlowPreviewModal (props: SenderPreviewModalProps) {
  return asyncOpenModal(
    import('./SenderPreviewModal.vue'),
    {
      id: 'animated-card-sender-preview-modal',
      ...props,
    },
  )
}

export async function openGreetingCardCustomizer (props: SenderFlowModalProps) {
  return asyncOpenModal(
    import('./SenderFlowModal.vue'),
    {
      id: 'animated-card-sender-flow-modal',
      ...props,
    },
  )
}

export type SimplePhotoEditorModalProps = {
  initialSlide?: GreetingCardStorySlide
  onDone?: (slide: GreetingCardStorySlide) => void
  onRemove?: () => void
}

export async function openSimplePhotoEditor (props: SimplePhotoEditorModalProps) {
  return asyncOpenModal(
    import('./SimplePhotoEditorModal.vue'),
    { ...props },
  )
}

export async function openGreetingCardsModal (
  greeting: OrderGiftGreeting,
  orderGift: OrderGiftTemplate,
  onCardSelected?: (selectedCard: GreetingCardWithCachedRiveFile, useFastLane?: boolean) => void,
) {
  return asyncOpenModal(
    import('./StepGreetingCardEditModalSelectCardModalV2.vue'),
    {
      id: 'greeting-card-select-modal',
      greeting,
      orderGift,
      onCardSelected,
    },
  )
}
